import type { Document } from "photoshop/dom/Document";
import type { Layer } from "photoshop/dom/Layer";

import { notify } from "../../api/photoshop";
import { photoshop } from "../../globals";
import {
	base64ToUint8Array,
	createBase64FromRawGrays,
	createBase64FromRawPixels,
} from "../../utils/jimp";
import { isCropToolActive } from "./check-crop-tool";

type Bounds = {
	left: number;
	top: number;
	right: number;
	bottom: number;
	width: number;
	height: number;
};

type TargetEditorImageContext = {
	doc: Document;
	executionContext: any;
	selBounds: Bounds;
	imageBase64: string;
	maskBase64?: string;
	sourceBounds: {
		left: number;
		top: number;
		right: number;
		bottom: number;
	};
	maskSourceBounds?: {
		left: number;
		top: number;
		right: number;
		bottom: number;
	};
	maskImageData?: any;
	matchedAspectRatio?: string;
};

type CustomResultHandlerContext = {
	doc: Document;
	resultBase64: string;
	selBounds: Bounds;
	sourceBounds: {
		left: number;
		top: number;
		right: number;
		bottom: number;
	};
	maskSourceBounds?: {
		left: number;
		top: number;
		right: number;
		bottom: number;
	};
	maskImageData?: any;
};

type TargetEditorImageOptions = {
	commandName: string;
	requireSelection?: boolean;
	requireSolidSelection?: boolean;
	mergeAllLayers?: boolean;
	requireLayer?: boolean;
	requireSingleLayer?: boolean;
	requireSelectionBounds?: boolean;
	includeMask?: boolean;
	applyMaskToResultLayer?: boolean;
	duplicateSingleLayer?: boolean;
	useLayerBoundsAsSource?: boolean;
	padToAspectRatio?: string;
	maxSide?: number;
	processImage: (context: TargetEditorImageContext) => Promise<string | string[] | false>;
	resultLayerName: string;
	customResultHandler?: (context: CustomResultHandlerContext) => Promise<void>;
	placeAsSmartObject?: boolean;
	preventSmartObjectTrim?: boolean;
};

type TargetEditorImageResult = {
	success: boolean;
};

export async function getTargetEditorImage(
	options: TargetEditorImageOptions,
): Promise<TargetEditorImageResult> {
	const {
		commandName,
		requireSelection = false,
		requireSolidSelection = false,
		mergeAllLayers = true,
		requireLayer = false,
		requireSingleLayer = false,
		requireSelectionBounds = false,
		includeMask = false,
		applyMaskToResultLayer = false,
		duplicateSingleLayer = false,
		useLayerBoundsAsSource = false,
		padToAspectRatio,
		maxSide = 3072,
		processImage,
		resultLayerName,
		customResultHandler,
		placeAsSmartObject = true,
		preventSmartObjectTrim = false,
	} = options;

	let success = false;

	if (isCropToolActive()) {
		await notify("Cannot generate images while the Crop tool is active.");
		return { success };
	}

	await photoshop.core.executeAsModal(
		async (executionContext) => {
			const doc = photoshop.app.activeDocument;
			if (!doc) {
				await notify("Open a PSD file first");
				return;
			}

			await doc.suspendHistory(async () => {
				let targetLayers = doc.activeLayers;
				let tempLayer: Layer | null = null;

				try {
					if (
						doc.mode !== "RGBColorMode" ||
						doc.bitsPerChannel !== "bitDepth8"
					) {
						await notify("Image mode must be RGB Color, 8bits/Channel");
						return;
					}

					if (requireSelection && doc.selection.bounds === null) {
						await notify("Select an area");
						return;
					}

					if (requireLayer && doc.activeLayers.length === 0) {
						await notify("Select a layer");
						return;
					}

					if (requireSingleLayer && doc.activeLayers.length !== 1) {
						await notify("Select only one layer");
						return;
					}

					if (
						(!requireLayer && doc.activeLayers.length === 0) ||
						mergeAllLayers
					) {
						targetLayers = doc.layers;
					}

					const isSelectionEmpty = doc.selection.bounds === null;
					const selBounds: Bounds = doc.selection.bounds ?? {
						left: 0,
						top: 0,
						right: doc.width,
						bottom: doc.height,
						width: doc.width,
						height: doc.height,
					};

					if (requireSelectionBounds && !doc.selection.bounds) {
						await notify("Selection area is empty");
						return;
					}

					if (
						doc.selection.bounds &&
						requireSolidSelection &&
						!doc.selection.solid
					) {
						await notify("Selection area must be a rectangle.");
						return;
					}

					let mergedLayer: Layer | undefined;

					if (!mergeAllLayers) {
						if (duplicateSingleLayer) {
							const selectedLayer = doc.activeLayers[0];
							const copiedLayer = await selectedLayer.duplicate();
							if (!copiedLayer) {
								await notify("Failed to duplicate layer");
								return;
							}
							copiedLayer.name = "Processing...";
							selectedLayer.selected = false;
							copiedLayer.selected = true;
							mergedLayer = copiedLayer;
						} else {
							const copiedLayers = await doc.duplicateLayers(targetLayers);

							await photoshop.app.batchPlay(
								[
									{
										_obj: "make",
										_target: [{ _ref: "layer" }],
										using: { _obj: "layer", name: "White Background" },
									},

									{
										_obj: "fill",
										using: { _enum: "fillContents", _value: "white" },
										opacity: { _unit: "percentUnit", _value: 100 },
										mode: { _enum: "blendMode", _value: "normal" },
									},
									{
										_obj: "move",
										_target: [
											{ _ref: "layer", _enum: "ordinal", _value: "targetEnum" },
										],
										to: {
											_ref: "layer",
											_enum: "ordinal",
											_value: "back",
										},
									},
								],
								{
									synchronousExecution: true,
									modalBehavior: "execute",
								},
							);
							const whiteLayer = doc.activeLayers[0];
							copiedLayers.push(whiteLayer);
							const groupLayer = await doc.groupLayers(copiedLayers);
							mergedLayer = await groupLayer?.merge();
						}

						if (!mergedLayer) {
							await notify("Failed to merge layers");
							return;
						}

						tempLayer = mergedLayer;
						if (!duplicateSingleLayer) {
							tempLayer.name = "Processing...";
							tempLayer.selected = true;
						}
					}

					const getPixelsOptions: any = {
						documentID: doc.id,
						applyAlpha: true,
					};

					if (!mergeAllLayers && tempLayer) {
						getPixelsOptions.layerID = tempLayer.id;
					}

					if (!useLayerBoundsAsSource) {
						getPixelsOptions.sourceBounds = {
							left: selBounds.left,
							top: selBounds.top,
							right: selBounds.right,
							bottom: selBounds.bottom,
						};
					}

					const { imageData, sourceBounds } =
						await photoshop.imaging.getPixels(getPixelsOptions);

					const pixelData = await imageData.getData({});

					const selWidth = selBounds.width;
					const selHeight = selBounds.height;
					let ratioPaddingWidth = 0;
					let ratioPaddingHeight = 0;
					let matchedAspectRatio: string | undefined;

					if (padToAspectRatio) {
						const bestRatio = padToAspectRatio === "auto"
							? findClosestAspectRatio(selWidth, selHeight)
							: padToAspectRatio;
						matchedAspectRatio = bestRatio;
						const [rw, rh] = bestRatio.split(":").map(Number);
						const targetRatio = rw / rh;
						const currentRatio = selWidth / selHeight;
						if (currentRatio < targetRatio) {
							// Need wider canvas: add horizontal padding
							const targetW = Math.round(selHeight * targetRatio);
							ratioPaddingWidth = targetW - selWidth;
						} else if (currentRatio > targetRatio) {
							// Need taller canvas: add vertical padding
							const targetH = Math.round(selWidth / targetRatio);
							ratioPaddingHeight = targetH - selHeight;
						}
					}

					const sourceWidth = sourceBounds.right - sourceBounds.left;
					const sourceHeight = sourceBounds.bottom - sourceBounds.top;

					const baseWidth = useLayerBoundsAsSource ? sourceWidth : selWidth;
					const baseHeight = useLayerBoundsAsSource ? sourceHeight : selHeight;
					const totalWidth = baseWidth + ratioPaddingWidth;
					const totalHeight = baseHeight + ratioPaddingHeight;
					let targetWidth = totalWidth;
					let targetHeight = totalHeight;

					if (maxSide && Math.max(totalWidth, totalHeight) > maxSide) {
						const scale = maxSide / Math.max(totalWidth, totalHeight);
						targetWidth = Math.round(totalWidth * scale);
						targetHeight = Math.round(totalHeight * scale);
					}

					const halfPadW = Math.floor(ratioPaddingWidth / 2);
					const halfPadH = Math.floor(ratioPaddingHeight / 2);

					const imageBase64 = await createBase64FromRawPixels(
						pixelData as Uint8Array,
						sourceWidth,
						sourceHeight,
						useLayerBoundsAsSource
							? undefined
							: {
									top: sourceBounds.top - selBounds.top + halfPadH,
									bottom:
										selBounds.bottom -
										sourceBounds.bottom +
										(ratioPaddingHeight - halfPadH),
									left: sourceBounds.left - selBounds.left + halfPadW,
									right:
										selBounds.right - sourceBounds.right + (ratioPaddingWidth - halfPadW),
									color: [255, 255, 255],
								},
						// Resize with Jimp
						{ width: targetWidth, height: targetHeight },
					);

					let maskBase64: string | undefined;
					let maskImageData: any;
					let maskSourceBounds:
						| { left: number; top: number; right: number; bottom: number }
						| undefined;

					if (includeMask && doc.selection.bounds) {
						const maskResult = await photoshop.imaging.getSelection({
							documentID: doc.id,
							sourceBounds: {
								left: selBounds.left,
								top: selBounds.top,
								right: selBounds.right,
								bottom: selBounds.bottom,
							},
						});
						maskImageData = maskResult.imageData;
						maskSourceBounds = maskResult.sourceBounds;

						const maskPixelData = await maskImageData.getData({});

						const maskWidth = maskSourceBounds.right - maskSourceBounds.left;
						const maskHeight = maskSourceBounds.bottom - maskSourceBounds.top;

						if (useLayerBoundsAsSource) {
							// Create mask in layer bounds coordinate space
							// Fill with black (0), then place selection mask at correct offset
							const layerMask = new Uint8Array(sourceWidth * sourceHeight).fill(
								0,
							);

							// Calculate where the selection mask should be placed within layer bounds
							const offsetX = maskSourceBounds.left - sourceBounds.left;
							const offsetY = maskSourceBounds.top - sourceBounds.top;

							for (let y = 0; y < maskHeight; y++) {
								for (let x = 0; x < maskWidth; x++) {
									const dstX = x + offsetX;
									const dstY = y + offsetY;
									if (
										dstX >= 0 &&
										dstX < sourceWidth &&
										dstY >= 0 &&
										dstY < sourceHeight
									) {
										layerMask[dstY * sourceWidth + dstX] =
											maskPixelData[y * maskWidth + x];
									}
								}
							}

							maskBase64 = await createBase64FromRawGrays(
								layerMask,
								sourceWidth,
								sourceHeight,
								undefined,
								{ width: targetWidth, height: targetHeight },
							);
						} else {
							maskBase64 = await createBase64FromRawGrays(
								maskPixelData as Uint8Array,
								maskWidth,
								maskHeight,
								{
									top: maskSourceBounds.top - selBounds.top + halfPadH,
									bottom:
										selBounds.bottom -
										maskSourceBounds.bottom +
										(ratioPaddingHeight - halfPadH),
									left: maskSourceBounds.left - selBounds.left + halfPadW,
									right:
										selBounds.right -
										maskSourceBounds.right +
										(ratioPaddingWidth - halfPadW),
									color: [0],
								},
								{ width: targetWidth, height: targetHeight },
							);
						}
					} else if (includeMask && !doc.selection.bounds) {
						maskBase64 = await createBase64FromRawGrays(
							new Uint8Array(targetWidth * targetHeight).fill(255),
							targetWidth,
							targetHeight,
						);
					}

					const result = await processImage({
						doc,
						executionContext,
						selBounds,
						imageBase64,
						maskBase64,
						sourceBounds,
						maskSourceBounds,
						maskImageData,
						matchedAspectRatio,
					});

					if (!result) {
						imageData.dispose();
						if (maskImageData) maskImageData.dispose();
						return;
					}

					const resultImages = Array.isArray(result) ? result : [result];

					if (customResultHandler) {
						await customResultHandler({
							doc,
							resultBase64: resultImages[0],
							selBounds,
							sourceBounds,
							maskSourceBounds,
							maskImageData,
						});
						imageData.dispose();
						if (maskImageData) maskImageData.dispose();
						success = true;
						return;
					}

					const targetBounds = useLayerBoundsAsSource
						? sourceBounds
						: selBounds;
					const putBounds = targetBounds;
					const putWidth = putBounds.right - putBounds.left;
					const putHeight = putBounds.bottom - putBounds.top;
					const createdLayers: Layer[] = [];

					if (!isSelectionEmpty) await doc.selection.deselect();

					for (let ri = 0; ri < resultImages.length; ri++) {
						const { width, height, rawPixels } = base64ToUint8Array(resultImages[ri]);

						let finalPixels = new Uint8Array(rawPixels);
						let finalWidth = width;
						let finalHeight = height;

						// Crop result image to match original selBounds aspect ratio
						if (padToAspectRatio && selWidth > 0 && selHeight > 0) {
							const originalRatio = selWidth / selHeight;
							const resultRatio = width / height;

							let cropW = width;
							let cropH = height;
							let cropX = 0;
							let cropY = 0;

							if (resultRatio > originalRatio) {
								cropW = Math.round(height * originalRatio);
								cropX = Math.round((width - cropW) / 2);
							} else if (resultRatio < originalRatio) {
								cropH = Math.round(width / originalRatio);
								cropY = Math.round((height - cropH) / 2);
							}

							if (cropW !== width || cropH !== height) {
								const croppedPixels = new Uint8Array(cropW * cropH * 4);
								for (let y = 0; y < cropH; y++) {
									const srcOffset = ((y + cropY) * width + cropX) * 4;
									const dstOffset = y * cropW * 4;
									croppedPixels.set(
										finalPixels.slice(srcOffset, srcOffset + cropW * 4),
										dstOffset,
									);
								}
								finalPixels = croppedPixels;
								finalWidth = cropW;
								finalHeight = cropH;
							}
						}

						// 배경 제거 시 Smart Object 트리밍 방지: 네 귀퉁이에 alpha=1 설정
						if (placeAsSmartObject && preventSmartObjectTrim && finalWidth > 0 && finalHeight > 0) {
							const corners = [
								0,
								(finalWidth - 1) * 4,
								(finalHeight - 1) * finalWidth * 4,
								((finalHeight - 1) * finalWidth + (finalWidth - 1)) * 4,
							];
							for (const offset of corners) {
								if (finalPixels[offset + 3] === 0) {
									finalPixels[offset + 3] = 1;
								}
							}
						}

						const resultImageData =
							await photoshop.imaging.createImageDataFromBuffer(finalPixels, {
								width: finalWidth,
								height: finalHeight,
								components: 4,
								colorSpace: "RGB",
							});

						const layerName = resultImages.length > 1
							? `${resultLayerName} ${ri + 1}`
							: resultLayerName;

						const newLayer = await doc.createPixelLayer({
							name: layerName,
						});

						if (!newLayer) {
							resultImageData.dispose();
							imageData.dispose();
							if (maskImageData) maskImageData.dispose();
							throw new Error("Failed to create layer");
						}

						await photoshop.imaging.putPixels({
							documentID: doc.id,
							layerID: newLayer.id,
							imageData: resultImageData,
							targetBounds: putBounds,
							replace: true,
						});

						if (placeAsSmartObject) {
							newLayer.selected = true;
							await photoshop.app.batchPlay(
								[{ _obj: "newPlacedLayer" }],
								{ synchronousExecution: true, modalBehavior: "execute" },
							);
						}

						if (finalWidth > 0 && finalHeight > 0) {
							const targetLayer = placeAsSmartObject ? doc.activeLayers[0] : newLayer;
							await targetLayer.scale(
								(putWidth / finalWidth) * 100,
								(putHeight / finalHeight) * 100,
								photoshop.constants.AnchorPosition.TOPLEFT,
							);
						}

						createdLayers.push(placeAsSmartObject ? doc.activeLayers[0] : newLayer);
						resultImageData.dispose();
					}

					if (applyMaskToResultLayer && maskImageData && maskSourceBounds) {
						await photoshop.imaging.putSelection({
							documentID: doc.id,
							imageData: maskImageData,
							targetBounds: maskSourceBounds,
							replace: true,
						});

						for (const layer of createdLayers) {
							const newLayerWidth = doc.width;
							const newLayerHeight = doc.height;
							const newLayerPixelData = new Uint8Array(
								newLayerWidth * newLayerHeight,
							).fill(0);
							const newLayerImageData =
								await photoshop.imaging.createImageDataFromBuffer(
									newLayerPixelData,
									{
										width: newLayerWidth,
										height: newLayerHeight,
										components: 1,
										colorSpace: "Grayscale",
									},
								);

							await photoshop.imaging.putLayerMask({
								documentID: doc.id,
								layerID: layer.id,
								imageData: newLayerImageData,
								replace: true,
							});
							await photoshop.imaging.putLayerMask({
								documentID: doc.id,
								layerID: layer.id,
								imageData: maskImageData,
								targetBounds: maskSourceBounds,
								replace: false,
							});

							newLayerImageData.dispose();
						}
					}

					if (!isSelectionEmpty) await doc.selection.selectRectangle(selBounds);

					imageData.dispose();
					if (maskImageData) maskImageData.dispose();

					if (createdLayers.length > 1) {
						for (const layer of createdLayers) {
							layer.selected = true;
						}
					}

					success = true;
				} catch (err) {
					console.error(err);
					await notify(`${err}`);
					for (const layer of targetLayers) {
						try {
							layer.selected = true;
						} catch {
							// Layer may have been deleted during processing
						}
					}
				} finally {
					if (tempLayer) {
						tempLayer.delete();
					}
				}
			}, commandName);
		},
		{
			commandName,
		},
	);

	return { success };
}

const SUPPORTED_RATIOS = [
	{ label: "1:1", value: 1 / 1 },
	{ label: "16:9", value: 16 / 9 },
	{ label: "9:16", value: 9 / 16 },
	{ label: "4:3", value: 4 / 3 },
	{ label: "3:4", value: 3 / 4 },
	{ label: "5:4", value: 5 / 4 },
	{ label: "4:5", value: 4 / 5 },
];

function findClosestAspectRatio(width: number, height: number): string {
	const currentRatio = width / height;
	let closest = SUPPORTED_RATIOS[0];
	let minDiff = Infinity;
	for (const ratio of SUPPORTED_RATIOS) {
		const diff = Math.abs(currentRatio - ratio.value);
		if (diff < minDiff) {
			minDiff = diff;
			closest = ratio;
		}
	}
	return closest.label;
}
